<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="''"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      :no-bottom-margin="true"
      @add="addUnitPrice"
    />
    <DxDataGrid
      :height="300"
      :data-source="localData.unit_prices"
      :showBorders="true"
      :ref="gridRefKey"
      :element-attr="gridAttributes"
      key-expr="season_uuid"
      :scrolling="{ mode: 'standard' }"
      :on-row-inserting="onRowInserting"
      :on-row-updating="onRowUpdating"
    >
      <DxColumn
        dataField="season_uuid"
        :caption="$t('works.work_list.season')"
        :lookup="{
          dataSource: getSeasonsLookup,
          valueExpr: 'uuid',
          displayExpr: 'name',
        }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        dataType="number"
        :format="this.getDxGridDefaultNumberFormat"
        dataField="unit_price"
        :caption="$t('common.unit_price') + ' / ha'"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxEditing mode="form" :allowUpdating="canEdit" :allowAdding="false" :allowDeleting="canDelete" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('common.unit_price'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="false" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxRequiredRule,
} from "devextreme-vue/data-grid";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import notify from "devextreme/ui/notify";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "WorkGroupUnitPrice",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxRequiredRule,
    GridPanelHeader,
  },
  props: {
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid work-group-unit-price-grid",
      },
      gridRefKey: "scalesGrid",
      localData: {
        unit_prices: this.data,
      },
    };
  },
  computed: {
    ...mapGetters({
      seasons: "seasons/seasons",
      getDxGridDefaultNumberFormat: "settings/getDxGridDefaultNumberFormat",
    }),
    config() {
      return {
        permission: permissions.WORK_GROUPS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getSeasons: "seasons/getSeasons",
    }),
    addUnitPrice() {
      this.$refs[this.gridRefKey]?.instance?.addRow();
    },
    getSeasonsLookup() {
      if (!this.seasons || (this.seasons.length ?? 0) === 0) {
        return [];
      }
      return this.seasons.map((season) => ({
        uuid: season.uuid,
        name: season.name,
      }));
    },
    round3(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? Number(n.toFixed(3)) : 0;
    },
    existsSeason(seasonUuid, excludeKey) {
      if (!seasonUuid) return false;
      return this.localData.unit_prices.some((r) => r.season_uuid === seasonUuid && r.season_uuid !== excludeKey);
    },
    onRowInserting(e) {
      const seasonUuid = e.data.season_uuid;
      if (this.existsSeason(seasonUuid)) {
        e.cancel = true;
        notify({
          message: this.$t("validation.duplicate_season"),
          type: "warning",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
      }
      if (e.data.unit_price != null) {
        e.data.unit_price = this.round3(e.data.unit_price);
      }
    },
    onRowUpdating(e) {
      const seasonUuid = e.newData.season_uuid !== undefined ? e.newData.season_uuid : e.oldData.season_uuid;
      if (this.existsSeason(seasonUuid, e.key)) {
        e.cancel = true;
        notify({
          message: this.$t("validation.duplicate_season"),
          type: "warning",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
      }
      if (e.newData.unit_price != null) {
        e.newData.unit_price = this.round3(e.newData.unit_price);
      }
    },
    getUnitPrices() {
      return this.localData.unit_prices;
    },
  },
  mounted() {
    if (!this.seasons || (this.seasons.length ?? 0) === 0) {
      this.getSeasons();
    }
  },
};
</script>
